import React, { useEffect, useState } from "react";
import styles from "./ConfirmPayment.module.scss";
import CommonLoadingSpinner from "../../../common/components/loading-spinner/CommonLoadingSpinnerForPage";
import { RoofType } from "../../../model/solar-recommendation-form/roofType";
import { toast } from "react-toastify";
import { CardIcon, PuzzleIcon } from "../../../assets/icons";
import { isCustomer } from "../../../guards/common/authentification-functions";
import { ApplicationRoutes } from "../../../routes";
import { useLocation, useNavigate } from "react-router-dom";
import { useLazyVerifyPaystackPaymentQuery } from "../../../service/paystack-service";
import { TransactionStatus } from "../../../model/transaction";
import { useGetPaymentConfirmationDetailsMutation } from "../../../service/order-service";

type Props = {};

const ConfirmPayment: React.FC<Props> = (props: Props) => {
	const navigate = useNavigate();
	const location = useLocation();

	const [paymentConfirmationDetails, { isLoading, data }] = useGetPaymentConfirmationDetailsMutation();

	const [orderId, setOrderId] = useState<string | null>(null);

	const [isWaitingToVerify, setIsWaitingToVerify] = useState<boolean>(true);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const order = params?.get("order");
		// if (status === AUTHORIZATION_STATUS.CARD_FAILURE || status === AUTHORIZATION_STATUS.CARD_SUCESS)
		if (order) {
			setOrderId(order);
		}
	}, [location]);

	const rooftopDetails = (roofType: RoofType, numberOfFloors?: number) => {
		let rooftopDetails = null;

		rooftopDetails = roofType.premiseType;

		if (numberOfFloors) {
			rooftopDetails += `, ${numberOfFloors} floors`;
		}

		rooftopDetails += `, ${roofType.name}`;

		return rooftopDetails;
	};

	const statusStyle = (status?: TransactionStatus) => {
		switch (status) {
			case "Completed":
				return styles.success;
			case "Failed":
				return styles.failed;
			default:
				return styles.pending;
		}
	};

	useEffect(() => {
		const confirmPaymentStatus = setTimeout(() => {
			if (!orderId) {
				return;
			}

			paymentConfirmationDetails({
				orderId: orderId,
			})
				.unwrap()
				.catch(error => {
					toast.error(error.data?.title || "Something went wrong");
				})
				.finally(() => {
					setIsWaitingToVerify(false);
				});
		}, 5000);

		return () => clearTimeout(confirmPaymentStatus);
	}, [orderId]);

	const handleContinueClick = () => {
		if (isCustomer()) {
			navigate(`/${ApplicationRoutes.DASHBOARD}`);
		} else {
			navigate(`/${ApplicationRoutes.STORE_FRONT}`);
		}
	};

	return (
		<div className={styles.loadingContainer}>
			<div className={styles.banner}></div>
			<CommonLoadingSpinner loading={isLoading || isWaitingToVerify}>
				<div className={styles.container}>
					<div className={styles.orderConfirmationContainer}>
						<h1>Payment Confirmation</h1>
						<p>Free Shipping only in Lagos</p>
						<button className={`standardButton ${styles.buyNowButton}`} onClick={handleContinueClick}>
							Continue
						</button>
					</div>
					{data?.transaction && (
						<div className={styles.transactionDetailsContainer}>
							<h2>Transaction Details</h2>
							<div className={styles.transactionDetails}>
								<div className={styles.item}>
									<p className={styles.label}>Transaction ID</p>
									<p className={styles.data}>{data?.transaction?.reference}</p>
								</div>
								<div className={styles.item}>
									<p className={styles.label}>Payment Status</p>
									{/* <div className={`${styles.data} ${styles.status} ${statusStyle(data?.transaction?.status)}`}>{data?.transaction?.status}</div> */}
								</div>
							</div>
						</div>
					)}
					<div className={styles.orderDetailsContainer}>
						<img className={styles.productImage} src={data?.solarProduct.images[0].imageUrl} alt="product" />
						<div className={styles.orderDetails}>
							<div className={styles.orderDetailsHeader}>
								<div className={styles.item}>
									<p>{data?.solarProduct.productName}</p>
									<p>{`₦${data?.orderInformation.productPrice.toLocaleString()}`}</p>
								</div>
							</div>
							<div className={styles.separator} />
							<div className={styles.orderDetailContainer}>
								<div className={styles.paymentPlanContainer}>
									<div className={styles.detailHeader}>
										<CardIcon />
										<h3>Payment Plan</h3>
									</div>
									{(() => {
										switch (data?.orderInformation.paymentMethod) {
											case "One Time Payment":
												return <b>One-Time Payment</b>;
											case "Installments":
												return (
													<div className={styles.monthlyInstallmentContainer}>
														<b>{data?.orderInformation.durationOfPaymentsInMonths} months Installment</b>
														<p>
															Total Payment <b> — {`₦${data?.orderInformation.total.toLocaleString()}`}</b>
														</p>
														<p>
															Initial deposit <b> — {`₦${data?.orderInformation.deposit.toLocaleString()}`}</b>
														</p>
														<p>
															Monthly Installment <b> — {`₦${data?.orderInformation.monthlyInstallment.toLocaleString()}`}</b>
														</p>
													</div>
												);
											default:
												return null;
										}
									})()}
								</div>
								{data?.orderInformation.roofType && (
									<div className={styles.addOnContainer}>
										<div className={styles.detailHeader}>
											<PuzzleIcon />
											<h3>Add-on</h3>
										</div>
										{data?.orderInformation.roofType && (
											<>
												<b>Installation</b>
												<p>
													{`${rooftopDetails(data?.orderInformation.roofType, data?.orderInformation.numberOfFloors)}`}
													<b> — {`₦${data?.orderInformation.installmentPrice?.toLocaleString()}`}</b>
												</p>
											</>
										)}
										{/* <b>Insurance</b>
									<p>
										Leadsway Insurance <b>— ₦50,000</b>
									</p> */}
									</div>
								)}
							</div>
						</div>
					</div>
					<div className={styles.separator} />
					<div className={styles.orderPriceContainer}>
						<div className={styles.orderPriceDetails}>
							<div className={styles.item}>
								<p>Subtotal</p>
								<b>{`₦${data?.orderInformation.subTotal.toLocaleString()}`}</b>
							</div>
							<div className={styles.item}>
								<p>Shipping</p>
								<b>{data?.orderInformation.shipping ? `₦${data?.orderInformation.shipping.toLocaleString()}` : "FREE"}</b>
							</div>
							<div className={styles.item}>
								<p>Estimated Tax</p>
								<b>{`₦${data?.orderInformation.tax.toLocaleString()}`}</b>
							</div>
							<div className={styles.separator} />
							<div className={`${styles.item} ${styles.bold}`}>
								<p>Total</p>
								<p>{`₦${data?.orderInformation.total.toLocaleString()}`}</p>
							</div>
							<button className={`standardButton ${styles.buyNowButton}`} onClick={handleContinueClick}>
								Continue
							</button>
						</div>
					</div>
				</div>
			</CommonLoadingSpinner>
		</div>
	);
};

export default ConfirmPayment;
